import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Pagination,
  Button,
  CircularProgress,
} from "@mui/material";
import { vendor_services_enquiry_list } from "../../../api/Enquiry";
import { get_vendor_store } from "../../../api/Role";
import { useUser } from "../../../context/LoginDetail";
import { useNavigate } from "react-router-dom";

const MyServiceEnquiryList = () => {
  const [enquiryList, setEnquiryList] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();
  const navigate = useNavigate();
  const [hasCompany, setHasCompany] = useState(false);
  const [checkLoading, setCheckLoading] = useState(true);

  const checkCompanyProfile = async () => {
    if (!user?.id) return;
    setCheckLoading(true);
    try {
      const res = await get_vendor_store(user.id);
      if (res?.data) {
        setHasCompany(true);
      } else {
        setHasCompany(false);
      }
    } catch (err) {
      setHasCompany(false);
    } finally {
      setCheckLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkCompanyProfile();
    }
  }, [user?.id]);

  const getEnquiryList = async () => {
    setLoading(true);
    try {
      const res = await vendor_services_enquiry_list();
      setEnquiryList(res?.data?.material_enquiry || res?.data?.service_enquiry || []);
      if (res?.data?.pagination?.per_page) {
        setRowsPerPage(res.data.pagination.per_page);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasCompany) {
      getEnquiryList();
    }
  }, [hasCompany]);

  const trimmedSearch = search.trim().toLowerCase();

  const filteredData = enquiryList.filter((item) => {
    if (!trimmedSearch) return true;

    const serviceName = item?.service?.service_name?.toString() || "";
    const customerName = item?.user?.name?.toString() || item?.name?.toString() || "";
    const quote = item?.quote?.toString() || "";

    return (
      serviceName.toLowerCase().includes(trimmedSearch) ||
      customerName.toLowerCase().includes(trimmedSearch) ||
      quote.toLowerCase().includes(trimmedSearch)
    );
  });

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  if (checkLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress sx={{ color: "#7E9F40" }} />
      </Box>
    );
  }

  if (!hasCompany) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, textAlign: "center", mt: 5 }}>
        <Typography variant="h6" gutterBottom>
          Please complete your Company Profile to add services.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/dashboard/company-profile")}
          sx={{ bgcolor: "#7E9F40", mt: 2, textTransform: "none", "&:hover": { bgcolor: "#6a8a35" } }}
        >
          Go to Company Profile
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={500}>
          My Service Enquiry List
        </Typography>

        <TextField
          label="Search Service Enquiry..."
          variant="outlined"
          size="small"
          sx={{ width: { xs: "100%", sm: "250px" } }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, py: 1 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.light", color: "white" }}>
              <TableCell><strong>Service Name</strong></TableCell>
              <TableCell><strong>Customer Name</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Quote</strong></TableCell>
              <TableCell><strong>Enquired On</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((enq) => (
                <TableRow key={enq.id}>
                  <TableCell>{enq?.service?.service_name || "-"}</TableCell>
                  <TableCell>{enq?.user?.name || enq?.name || "-"}</TableCell>
                  <TableCell>{enq?.user?.phone || enq?.phone || "-"}</TableCell>
                  <TableCell>{enq?.quote || "-"}</TableCell>
                  <TableCell>
                    {enq?.created_at ? new Date(enq.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    }) : "-"}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No enquiries found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filteredData.length > rowsPerPage && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default MyServiceEnquiryList;
